import { useCallback, useEffect, useRef, useState } from 'react';

import { requestAudioAPI } from '../lib/api';
import { config } from '../lib/config';
import type { Prosody, VoiceDataType } from '../lib/types';
import { PLAY_STATE } from '../lib/types';
import {
  getEmotionFromStyle,
  getStaticAudioFileName,
  textToSSML,
} from '../lib/utils';
import { useVoices } from '../providers/VoicesProvider';
import { Style } from '../component/elements/StyleJigsaw';
import Cookies from 'js-cookie';
import { useFeatureFlag } from '../lib/useFeatureFlags';

export const numGenerationKey = 'num_generation';
interface Error {
  message: string;
  type: 'rateLimit' | 'general';
}

const getRecommendedModel = (models: string[], languageCode: string) => {
  if (languageCode.startsWith('en') && models.includes('simba-english'))
    return 'simba-english';
  else if (models.includes('simba-multilingual')) {
    return 'simba-multilingual';
  } else {
    return 'simba-base';
  }
};

export const useAudioPlayer = (
  currentContent: string,
  currentStyle: Style | null,
  prosody: Prosody,
  currentVoice: VoiceDataType,
) => {
  const generations = Number(Cookies.get(numGenerationKey));
  const [playerState, setPlayerState] = useState<PLAY_STATE>(
    PLAY_STATE.STOPPED,
  );
  const audioRef = useRef<HTMLAudioElement | null>();
  const [audioDataUrl, setAudioDataUrl] = useState<string>();
  const [showReplay, setShowReplay] = useState(false);
  const [isContentChanged, setIsContentChanged] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [showUpsell, setShowUpsell] = useState(false);
  const { waitForToken, staticAudioFilesManifest } = useVoices();
  const [numGeneration, setNumGeneration] = useState<number>(
    isNaN(generations) ? 0 : generations,
  );
  const { variant: audioGenerationLimit } = useFeatureFlag(
    'vo_demo_generationLimit',
    -1,
  );

  const clearErrorToast = () => {
    setTimeout(() => {
      setError(null);
    }, 3000);
  };

  const playText = async () => {
    setPlayerState(PLAY_STATE.LOADING);
    audioRef.current?.pause();

    try {
      const audioUrl = await loadAudioDataUrl();
      if (audioUrl) playAudio(audioUrl);
    } catch (error) {
      setPlayerState(PLAY_STATE.STOPPED);
      if (error instanceof Error && error.message.includes('limit exceeded')) {
        setShowUpsell(true);
        setPlayerState(PLAY_STATE.STOPPED);
      } else {
        setError({
          type: 'general',
          message: 'An error occurred. Please try again later.',
        });
        clearErrorToast();
      }
    }
  };

  const loadAudioDataUrl = useCallback(async () => {
    const token = await waitForToken();
    const emotion = getEmotionFromStyle();
    const inputSSML = textToSSML(currentContent, {
      emotion,
    });

    const cachedAudioUrl = await getStaticAudioUrl(
      currentVoice.name,
      currentVoice.languageCode,
      emotion ?? null,
      currentContent,
    );

    if (staticAudioFilesManifest.includes(cachedAudioUrl.split('/').pop()!)) {
      setAudioDataUrl(cachedAudioUrl);
      return cachedAudioUrl;
    }

    if (audioGenerationLimit !== -1 && numGeneration >= audioGenerationLimit) {
      setPlayerState(PLAY_STATE.STOPPED);
      return;
    }

    Cookies.set(numGenerationKey, String(numGeneration + 1));
    setNumGeneration(numGeneration + 1);
    const audioUrl = await requestAudioAPI(token, {
      input: inputSSML,
      voice_id: currentVoice.name,
      model: getRecommendedModel(
        currentVoice.models,
        currentVoice.languageCode,
      ),
      language: currentVoice.languageCode,
    });
    setAudioDataUrl(audioUrl);
    return audioUrl;
  }, [currentVoice, waitForToken, currentContent, currentStyle, numGeneration]);

  const handlePlayText = () => {
    if (!currentContent) return null;
    const currentCount = 0;
    if (currentCount < 3) {
      playText().catch((err: Error) => {
        throw new Error(err.message);
      });
    } else {
      setShowUpsell(true);
      return null;
    }
  };

  const handlePlaybackEnd = () => {
    setPlayerState(PLAY_STATE.STOPPED);
    setShowReplay(true);
  };

  const stopAndReset = () => {
    audioRef.current?.pause();
    setAudioDataUrl(undefined);
    audioRef.current = null;
    setPlayerState(PLAY_STATE.STOPPED);
    setShowReplay(false);
    setIsContentChanged(false);
  };

  const playAudio = (audioUrl: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.removeEventListener('ended', handlePlaybackEnd);
      audioRef.current = null;
    }

    const newAudio = new Audio(audioUrl);
    newAudio.addEventListener('ended', () => {
      handlePlaybackEnd();
    });
    audioRef.current = newAudio;
    audioRef.current.playbackRate = prosody.rate;
    newAudio.play().catch((err: Error) => {
      throw new Error(err.message);
    });
  };

  const togglePlayback = () => {
    setShowReplay(false);
    const isPlaying = !audioRef?.current?.paused && !audioRef?.current?.ended;
    if (audioRef.current && audioDataUrl) {
      if (isPlaying) {
        audioRef.current.pause();
        setPlayerState(PLAY_STATE.STOPPED);
      } else {
        audioRef.current.play().catch((err: Error) => {
          throw new Error(err.message);
        });
        setPlayerState(PLAY_STATE.PLAYING);
      }
    } else {
      handlePlayText();
    }
  };

  useEffect(() => {
    const audio = audioRef.current;

    if (audio) {
      const handleWaiting = () => setPlayerState(PLAY_STATE.LOADING);
      const handleCanPlay = () => setPlayerState(PLAY_STATE.PLAYING);

      audio.addEventListener('waiting', handleWaiting);
      audio.addEventListener('canplay', handleCanPlay);

      return () => {
        audio.removeEventListener('waiting', handleWaiting);
        audio.removeEventListener('canplay', handleCanPlay);
      };
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [audioRef.current]); // this value is required in dependency but eslint is preventing this

  useEffect(() => {
    return () => {
      if (audioRef.current?.currentTime) audioRef.current.currentTime = 0;
      audioRef.current?.pause();
    };
  }, []);

  return {
    playAudio,
    stopAndReset,
    togglePlayback,
    playerState,
    showReplay,
    audioRef,
    error,
    setIsContentChanged,
    isContentChanged,
    showUpsell,
    setShowUpsell,
    setPlayerState,
    audioDataUrl,
    loadAudioDataUrl,
    numGeneration,
  };
};

async function getStaticAudioUrl(
  voiceId: string,
  locale: string,
  emotion: string | null,
  text: string,
): Promise<string> {
  const fileName = await getStaticAudioFileName(
    voiceId,
    locale,
    emotion?.toLowerCase() ?? 'neutral',
    text,
  );
  return `${config.static.public_base_url}/assets/audio/${fileName}`;
}
